import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { collection, query, where, getDocs, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { 
  Box, 
  Typography, 
  TextField, 
  Autocomplete, 
  Button, 
  IconButton, 
  Chip, 
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme,
  createFilterOptions,
  Tooltip,
  Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SportsMmaIcon from '@mui/icons-material/SportsMma';
import GroupsIcon from '@mui/icons-material/Groups';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useNavigate } from 'react-router-dom';
import { setFilters } from './filterSlice';

const FilterPage = () => {
  const [tournamentNames, setTournamentNames] = useState([]);
  const [teamNames, setTeamNames] = useState([]);
  const [gameTitles, setGameTitles] = useState([]);
  const [selectedTournaments, setSelectedTournaments] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [selectedGames, setSelectedGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const user = useSelector(state => state.auth.user);
  const currentFilters = useSelector(state => state.filters);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      setLoading(true);
      try {
        // 大会名の取得
        const tournamentsRef = collection(db, 'tournamentNames');
        const tournamentsSnap = await getDocs(tournamentsRef);
        const tournamentOptions = [];
        tournamentsSnap.forEach(doc => {
          const data = doc.data();
          tournamentOptions.push({
            label: data.name,
            value: data.name
          });
        });
        setTournamentNames(tournamentOptions);

        // チーム名の取得
        const teamsRef = collection(db, 'teamNames');
        const teamsSnap = await getDocs(teamsRef);
        const teamOptions = [];
        teamsSnap.forEach(doc => {
          const data = doc.data();
          teamOptions.push({
            label: data.name,
            value: data.name
          });
        });
        setTeamNames(teamOptions);

        // ゲームタイトルの設定
        const CATEGORIES = [
          'League of Legends',
          'VALORANT',
          'Apex Legends',
          'PUBG',
          'Fortnite',
          'Overwatch 2',
          'Street Fighter 6',
          'FIFA 24',
          'Call of Duty',
          'Dota 2',
          'Counter-Strike 2',
          "イベント",
          'その他',
        ];
        const gameOptions = CATEGORIES.map(game => ({
          label: game,
          value: game
        }));
        setGameTitles(gameOptions);

        // 現在のReduxストアのフィルター設定を反映
        if (currentFilters) {
          const tournaments = currentFilters.tournamentNames?.map(name => ({ label: name, value: name })) || [];
          const teams = currentFilters.teamNames?.map(name => ({ label: name, value: name })) || [];
          const games = currentFilters.gameTitles?.map(name => ({ label: name, value: name })) || [];
          
          setSelectedTournaments(tournaments);
          setSelectedTeams(teams);
          setSelectedGames(games);
        }
      } catch (error) {
        console.error('フィルターオプションの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFilterOptions();
  }, [user, currentFilters]);

  const handleSaveFilters = async () => {
    if (!user) return;

    setSaving(true);
    try {
      const userFiltersRef = collection(db, 'userFilters');
      const q = query(userFiltersRef, where('userId', '==', user.uid));
      const userFiltersSnap = await getDocs(q);

      const filterData = {
        userId: user.uid,
        tournamentNames: selectedTournaments.map(item => item.value),
        teamNames: selectedTeams.map(item => item.value),
        gameTitles: selectedGames.map(item => item.value),
        updatedAt: new Date()
      };

      if (userFiltersSnap.empty) {
        await addDoc(userFiltersRef, filterData);
      } else {
        const docRef = doc(db, 'userFilters', userFiltersSnap.docs[0].id);
        await updateDoc(docRef, filterData);
      }

      // Reduxストアを更新
      dispatch(setFilters({
        tournamentNames: filterData.tournamentNames,
        teamNames: filterData.teamNames,
        gameTitles: filterData.gameTitles
      }));

      setShowSuccess(true);
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error) {
      console.error('フィルター設定の保存に失敗しました:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClearAll = () => {
    setSelectedTournaments([]);
    setSelectedTeams([]);
    setSelectedGames([]);
  };

  const totalFilters = selectedTournaments.length + selectedTeams.length + selectedGames.length;
  const hasChanges = 
    JSON.stringify(selectedTournaments.map(t => t.value)) !== JSON.stringify(currentFilters.tournamentNames || []) ||
    JSON.stringify(selectedTeams.map(t => t.value)) !== JSON.stringify(currentFilters.teamNames || []) ||
    JSON.stringify(selectedGames.map(g => g.value)) !== JSON.stringify(currentFilters.gameTitles || []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: theme.palette.neon.cyan }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      padding: isMobile ? 1 : 2,
      maxWidth: '700px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: theme.palette.background.default,
      pt: isMobile ? 2 : 3, // 上部の余白を大幅に削減
      pb: 2,
    }}>
      {/* ヘッダー部分 */}
      <Paper sx={{ 
        position: 'sticky',
        top: isMobile ? 0 : 10, // モバイルでは画面上部に固定
        zIndex: 10, // z-indexを上げて他の要素より前面に
        mb: isMobile ? 2 : 3,
        borderRadius: 0,
        overflow: 'hidden',
        backgroundColor: 'rgba(37, 37, 48, 0.98)',
        border: `2px solid ${theme.palette.neon.cyan}`,
        boxShadow: `0 0 30px ${theme.palette.neon.cyan}`,
        // 上部に影を追加してスクロール時の視認性を向上
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -10,
          left: 0,
          right: 0,
          height: 10,
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent)',
          pointerEvents: 'none',
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: isMobile ? 1.5 : 2,
          borderBottom: `1px solid rgba(0, 255, 255, 0.2)`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: isMobile ? 0.5 : 1, flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={() => navigate(-1)} 
                sx={{ 
                  mr: 1, 
                  p: isMobile ? 0.5 : 1,
                  color: theme.palette.neon.cyan,
                  border: `1px solid ${theme.palette.neon.cyan}`,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 255, 0.1)',
                    transform: 'scale(1.1)',
                  }
                }}
              >
                <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
              <FilterListIcon sx={{ mr: 1, color: theme.palette.neon.cyan, fontSize: isMobile ? 20 : 24 }} />
              <Typography 
                variant={isMobile ? "body1" : "h6"} 
                sx={{ 
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  color: theme.palette.neon.cyan,
                  textShadow: `0 0 10px ${theme.palette.neon.cyan}`,
                  whiteSpace: 'nowrap',
                }}
              >
                フィルター設定
              </Typography>
            </Box>
            {totalFilters > 0 && !isMobile && (
              <Chip 
                label={`${totalFilters}件 選択中`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 0, 255, 0.1)',
                  borderColor: theme.palette.neon.magenta,
                  color: theme.palette.neon.magenta,
                  border: '1px solid',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                }}
              />
            )}
          </Box>
          {totalFilters > 0 && (
            <Button
              startIcon={!isMobile && <ClearIcon />}
              onClick={handleClearAll}
              size="small"
              sx={{ 
                fontSize: isMobile ? '0.7rem' : '0.875rem',
                minWidth: 'auto',
                px: isMobile ? 0.8 : 2,
                py: isMobile ? 0.5 : 0.75,
                color: theme.palette.neon.pink,
                borderColor: theme.palette.neon.pink,
                border: '1px solid',
                backgroundColor: 'rgba(255, 0, 102, 0.1)',
                fontWeight: 700,
                letterSpacing: '0.05em',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 0, 102, 0.2)',
                  borderColor: theme.palette.neon.pink,
                  transform: 'scale(1.05)',
                },
                '& .MuiButton-startIcon': {
                  marginRight: isMobile ? 0 : 1,
                  marginLeft: 0,
                }
              }}
            >
              {isMobile ? 'クリア' : 'すべてクリア'}
            </Button>
          )}
        </Box>
        {currentFilters.isActive && (
          <Alert 
            severity="info" 
            sx={{ 
              borderRadius: 0, 
              py: isMobile ? 0.5 : 1,
              backgroundColor: 'rgba(0, 170, 255, 0.1)',
              color: theme.palette.neon.cyan,
              border: `1px solid ${theme.palette.neon.cyan}`,
              '& .MuiAlert-icon': {
                color: theme.palette.neon.cyan,
              }
            }}
          >
            <Typography variant={isMobile ? "caption" : "body2"} sx={{ fontWeight: 600, letterSpacing: '0.05em' }}>
              現在フィルターが適用されています
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* フィルターコンテンツ */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
        {/* フィルター説明 */}
        <Paper sx={{
          p: isMobile ? 1.5 : 2,
          borderRadius: 0,
          backgroundColor: 'rgba(37, 37, 48, 0.98)',
          border: `1px solid rgba(255, 255, 255, 0.2)`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
        }}>
          <InfoOutlinedIcon sx={{ 
            color: theme.palette.neon.cyan, 
            fontSize: isMobile ? 18 : 20,
            mt: 0.2
          }} />
          <Box>
            <Typography variant={isMobile ? "caption" : "body2"} sx={{ 
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: 1.6,
            }}>
              カレンダーに表示する大会を絞り込むことができます。
              {!isMobile && <br />}
              お気に入りの大会、チーム、ゲームタイトルを選択して、
              あなただけのカスタマイズされたスケジュールを作成しましょう。
            </Typography>
          </Box>
        </Paper>
        {/* Tournament Filter */}
        <Paper sx={{ 
          p: isMobile ? 2 : 3, 
          borderRadius: 0,
          backgroundColor: 'rgba(37, 37, 48, 0.98)',
          border: `2px solid rgba(0, 255, 255, 0.3)`,
          boxShadow: `0 0 20px rgba(0, 255, 255, 0.2)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.neon.cyan}, transparent)`,
            animation: 'scan 3s linear infinite',
          },
          '@keyframes scan': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1.5 : 2, gap: 1 }}>
            <SportsMmaIcon sx={{ 
              color: theme.palette.neon.cyan, 
              fontSize: isMobile ? 20 : 24,
            }} />
            <Typography 
              variant={isMobile ? "body1" : "subtitle1"} 
              sx={{ 
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: theme.palette.neon.cyan,
                textShadow: `0 0 5px ${theme.palette.neon.cyan}`,
              }}
            >
              大会フィルター
            </Typography>
            <Tooltip title="特定の大会のみを表示したい場合に使用します" placement="top">
              <HelpOutlineIcon sx={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                fontSize: isMobile ? 16 : 18,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.neon.cyan,
                }
              }} />
            </Tooltip>
          </Box>
          <Autocomplete
            multiple
            value={selectedTournaments}
            onChange={(event, newValue) => setSelectedTournaments(newValue)}
            options={tournamentNames}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            filterOptions={createFilterOptions({
              matchFrom: 'any',
              stringify: (option) => option.label,
            })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.label}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{ 
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    borderColor: theme.palette.neon.cyan,
                    color: theme.palette.neon.cyan,
                    backgroundColor: 'rgba(0, 255, 255, 0.1)',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 255, 255, 0.2)',
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="大会名を検索..." 
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                helperText={selectedTournaments.length > 0 ? `${selectedTournaments.length}件の大会を選択中` : '大会名を入力して検索 • 複数選択可能'}
                FormHelperTextProps={{ 
                  sx: { 
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    color: 'rgba(255, 255, 255, 0.5)',
                    letterSpacing: '0.02em',
                  } 
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(26, 26, 31, 0.6)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 255, 255, 0.3)',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 255, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.neon.cyan,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#ffffff',
                  },
                }}
              />
            )}
          />
        </Paper>

        {/* Team Filter */}
        <Paper sx={{ 
          p: isMobile ? 2 : 3, 
          borderRadius: 0,
          backgroundColor: 'rgba(37, 37, 48, 0.98)',
          border: `2px solid rgba(255, 0, 255, 0.3)`,
          boxShadow: `0 0 20px rgba(255, 0, 255, 0.2)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.neon.magenta}, transparent)`,
            animation: 'scan 3s linear infinite',
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1.5 : 2, gap: 1 }}>
            <GroupsIcon sx={{ 
              color: theme.palette.neon.magenta, 
              fontSize: isMobile ? 20 : 24,
            }} />
            <Typography 
              variant={isMobile ? "body1" : "subtitle1"} 
              sx={{ 
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: theme.palette.neon.magenta,
                textShadow: `0 0 5px ${theme.palette.neon.magenta}`,
              }}
            >
              チームフィルター
            </Typography>
            <Tooltip title="お気に入りのチームの試合のみを表示したい場合に使用します" placement="top">
              <HelpOutlineIcon sx={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                fontSize: isMobile ? 16 : 18,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.neon.magenta,
                }
              }} />
            </Tooltip>
          </Box>
          <Autocomplete
            multiple
            value={selectedTeams}
            onChange={(event, newValue) => setSelectedTeams(newValue)}
            options={teamNames}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            filterOptions={createFilterOptions({
              matchFrom: 'any',
              stringify: (option) => option.label,
            })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.label}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{ 
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    borderColor: theme.palette.neon.magenta,
                    color: theme.palette.neon.magenta,
                    backgroundColor: 'rgba(255, 0, 255, 0.1)',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 0, 255, 0.2)',
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="チーム名を検索..." 
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                helperText={selectedTeams.length > 0 ? `${selectedTeams.length}件のチームを選択中` : 'チーム名を入力して検索 • 複数選択可能'}
                FormHelperTextProps={{ 
                  sx: { 
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    color: 'rgba(255, 255, 255, 0.5)',
                    letterSpacing: '0.02em',
                  } 
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(26, 26, 31, 0.6)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 0, 255, 0.3)',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 0, 255, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.neon.magenta,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#ffffff',
                  },
                }}
              />
            )}
          />
        </Paper>

        {/* Game Title Filter */}
        <Paper sx={{ 
          p: isMobile ? 2 : 3, 
          borderRadius: 0,
          backgroundColor: 'rgba(37, 37, 48, 0.98)',
          border: `2px solid rgba(0, 255, 0, 0.3)`,
          boxShadow: `0 0 20px rgba(0, 255, 0, 0.2)`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${theme.palette.neon.lime}, transparent)`,
            animation: 'scan 3s linear infinite',
          },
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1.5 : 2, gap: 1 }}>
            <SportsEsportsIcon sx={{ 
              color: theme.palette.neon.lime, 
              fontSize: isMobile ? 20 : 24,
            }} />
            <Typography 
              variant={isMobile ? "body1" : "subtitle1"} 
              sx={{ 
                fontWeight: 700,
                letterSpacing: '0.05em',
                color: theme.palette.neon.lime,
                textShadow: `0 0 5px ${theme.palette.neon.lime}`,
              }}
            >
              ゲームタイトルフィルター
            </Typography>
            <Tooltip title="興味のあるゲームタイトルの大会のみを表示したい場合に使用します" placement="top">
              <HelpOutlineIcon sx={{ 
                color: 'rgba(255, 255, 255, 0.5)', 
                fontSize: isMobile ? 16 : 18,
                cursor: 'pointer',
                '&:hover': {
                  color: theme.palette.neon.lime,
                }
              }} />
            </Tooltip>
          </Box>
          <Autocomplete
            multiple
            value={selectedGames}
            onChange={(event, newValue) => setSelectedGames(newValue)}
            options={gameTitles}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            filterOptions={createFilterOptions({
              matchFrom: 'any',
              stringify: (option) => option.label,
            })}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.label}
                  size="small"
                  {...getTagProps({ index })}
                  sx={{ 
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    borderColor: theme.palette.neon.lime,
                    color: theme.palette.neon.lime,
                    backgroundColor: 'rgba(0, 255, 0, 0.1)',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 255, 0, 0.2)',
                    }
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="ゲームタイトルを検索..." 
                variant="outlined"
                size={isMobile ? "small" : "medium"}
                helperText={selectedGames.length > 0 ? `${selectedGames.length}件のゲームを選択中` : 'ゲームタイトルを入力して検索 • 複数選択可能'}
                FormHelperTextProps={{ 
                  sx: { 
                    fontSize: isMobile ? '0.7rem' : '0.75rem',
                    color: 'rgba(255, 255, 255, 0.5)',
                    letterSpacing: '0.02em',
                  } 
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(26, 26, 31, 0.6)',
                    '& fieldset': {
                      borderColor: 'rgba(0, 255, 0, 0.3)',
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 255, 0, 0.5)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.neon.lime,
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    color: '#ffffff',
                  },
                }}
              />
            )}
          />
        </Paper>

        {/* 使用例 */}
        <Paper sx={{
          p: isMobile ? 1.5 : 2,
          borderRadius: 0,
          backgroundColor: 'rgba(37, 37, 48, 0.98)',
          border: `1px solid rgba(255, 255, 255, 0.2)`,
        }}>
          <Typography variant={isMobile ? "caption" : "body2"} sx={{ 
            fontWeight: 600,
            color: theme.palette.neon.cyan,
            mb: 1,
          }}>
            💡 フィルターの使用例
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              • 「VALORANT」の大会のみ表示 → ゲームタイトルで「VALORANT」を選択
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              • 特定のチームを応援 → チームフィルターでチーム名を選択
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              • 複数の条件を組み合わせて、自分だけのスケジュールを作成
            </Typography>
          </Box>
        </Paper>

        {/* Apply Button */}
        <Box sx={{ 
          position: 'sticky', 
          bottom: 0, 
          backgroundColor: theme.palette.background.default, 
          pt: 2, 
          pb: 2,
          px: isMobile ? 1 : 0,
          // 下部に影を追加
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -10,
            left: 0,
            right: 0,
            height: 10,
            background: 'linear-gradient(to top, rgba(0,0,0,0.3), transparent)',
            pointerEvents: 'none',
          }
        }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSaveFilters}
            disabled={saving || !hasChanges}
            startIcon={saving ? <CircularProgress size={20} sx={{ color: theme.palette.neon.cyan }} /> : <CheckCircleIcon />}
            sx={{ 
              height: isMobile ? 44 : 48,
              fontSize: isMobile ? '0.9rem' : '1rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              borderRadius: 0,
              background: `linear-gradient(45deg, ${theme.palette.neon.cyan} 0%, ${theme.palette.neon.magenta} 100%)`,
              boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
              border: `2px solid ${theme.palette.neon.cyan}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.5s ease',
              },
              '&:hover::before': {
                left: '100%',
              },
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 0 30px ${theme.palette.neon.cyan}`,
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                border: '2px solid rgba(255, 255, 255, 0.2)',
              }
            }}
          >
            {saving ? '保存中...' : hasChanges ? 'フィルターを適用' : '変更なし'}
          </Button>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            <Typography 
              variant="caption" 
              sx={{ 
                textAlign: 'center', 
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: isMobile ? '0.65rem' : '0.7rem',
              }}
            >
              {totalFilters === 0 
                ? 'フィルターを設定すると、選択した条件に一致する大会のみが表示されます'
                : hasChanges 
                  ? '「フィルターを適用」をクリックして変更を保存してください'
                  : '現在のフィルター設定が適用されています'
              }
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Success Snackbar - 位置を下部に変更 */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // 上部から下部に変更
        sx={{
          bottom: isMobile ? 70 : 80, // 下部からの距離を調整
        }}
      >
        <Alert 
          severity="success" 
          sx={{ 
            width: '100%',
            backgroundColor: 'rgba(0, 255, 0, 0.1)',
            color: theme.palette.neon.lime,
            border: `1px solid ${theme.palette.neon.lime}`,
            borderRadius: 0,
            fontWeight: 700,
            letterSpacing: '0.05em',
            '& .MuiAlert-icon': {
              color: theme.palette.neon.lime,
            }
          }}
        >
          フィルターが正常に適用されました
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FilterPage;
